import type { Platform } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { PlatformIcon } from '@/components/badges';
import { cn } from '@/lib/utils';
import { PLATFORM_LABEL } from './buildHelpers';
import { ALL_PLATFORMS } from './runBuildModel';
import { RequiredLabel } from './RunBuildEnvironment';

interface PlatformPickerProps {
  available: Platform[];
  selected: Platform[];
  profileSlug: string | undefined;
  error: string | undefined;
  onToggle: (platform: Platform) => void;
}

/** Platform checkboxes: a platform the selected profile has no row for is shown but disabled. */
export function PlatformPicker({ available, selected, profileSlug, error, onToggle }: PlatformPickerProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <RequiredLabel>Target platforms</RequiredLabel>
      <div className="grid grid-cols-2 gap-2">
        {ALL_PLATFORMS.map((pl) => (
          <PlatformOption key={pl} platform={pl} available={available.includes(pl)} on={selected.includes(pl)} profileSlug={profileSlug} onToggle={onToggle} />
        ))}
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

interface PlatformOptionProps {
  platform: Platform;
  available: boolean;
  on: boolean;
  profileSlug: string | undefined;
  onToggle: (platform: Platform) => void;
}

function PlatformOption({ platform: pl, available, on, profileSlug, onToggle }: PlatformOptionProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={!available}
      data-testid={`run-build-platform-${pl}`}
      title={available ? undefined : `No ${PLATFORM_LABEL[pl]} profile with slug ${profileSlug ?? ''}`}
      onClick={() => onToggle(pl)}
      className={cn(
        'flex h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors duration-150',
        on ? 'border-primary/40 bg-primary/10 text-foreground' : 'bg-background text-muted-foreground hover:border-border-strong',
        !available && 'cursor-not-allowed opacity-40',
      )}
    >
      <PlatformIcon platform={pl} size={20} />
      {PLATFORM_LABEL[pl]}
      <Icon name={on ? 'check_circle' : 'radio_button_unchecked'} size={18} fill={on} className={cn('ml-auto', on ? 'text-primary' : 'text-subtle')} />
    </button>
  );
}
